import { assert } from "lib/util/assert";
import { Action } from "lib/action/types";
import { Renderer } from "lib/renderer";
import { Query } from "src/gqty";

const modules = {
  renderers: import.meta.glob<{ default: Renderer<any, any> }>(
    "./*/renderers/*.{ts,tsx}"
  ),
  actions: import.meta.glob<{ default: Action<any> }>("./*/actions/*.{ts,tsx}"),
  records: import.meta.glob<{ default: any }>("./*/records/*.{ts,tsx}"),
  queries: import.meta.glob<{ default: (query: Query) => any }>(
    "./*/queries/*.{ts,tsx}"
  ),
};

type ModuleLoader<T> = () => Promise<{ default: T }>;

type SubRegistry<T> = {
  items: Record<string, ModuleLoader<T>>;
  getQueryNameAsync: (id: string) => Promise<keyof Query>;
  getAsync: (id: string) => Promise<T>;
  get: (id: string, onSuccess: (item: T) => void) => void;
  getFilteredAsync: (
    filterFn: (key: string) => boolean
  ) => Promise<Record<string, T>>;
  getByRecordTypeAsync: (recordType: string) => Promise<Record<string, T>>;
  has: (id: string) => boolean;
};

function buildRegistry<
  TModules extends Record<string, () => Promise<{ default: any }>>,
>(
  modules: TModules
): SubRegistry<Awaited<ReturnType<TModules[keyof TModules]>>["default"]> {
  const items: Record<string, () => Promise<{ default: any }>> = {};
  const regex = new RegExp(`^\\.\\/([^/]+)\\/[^/]+\\/([^/.]+)\\.tsx?$`);

  Object.entries(modules).forEach(([path, loader]) => {
    const match = path.match(regex);
    if (!match) return;
    const [, domain, moduleName] = match;
    assert(domain);
    const key = `${domain.charAt(0).toUpperCase() + domain.slice(1)}.${moduleName}`;
    items[key] = loader;
  });

  const getAsync = async (id: string) => {
    const loader = items[id];
    assert(loader);
    const mod = await loader();

    if (typeof mod.default === "object") {
      return { ...mod.default, id }; // return object with id
    }

    return mod.default;
  };

  const getQueryNameAsync = async (id: string) => {
    const modDefault = await getAsync(id);

    let result: string | null = null;
    const handler = {
      get(_target: any, key: string | symbol) {
        if (typeof key === "string") {
          result = key;
          return new Proxy(() => {}, handler);
        }
        return undefined;
      },
    };
    const proxy = new Proxy({}, handler);

    modDefault(proxy);
    assert(result, "No query field accessed");

    return result;
  };

  return {
    items,
    getQueryNameAsync,
    getAsync,
    get: (id, onSuccess) => {
      getAsync(id).then(onSuccess);
    },
    getFilteredAsync: async (filterFn) => {
      const entries = await Promise.all(
        Object.keys(items)
          .filter(filterFn)
          .map(async (key) => [key, await getAsync(key)] as const)
      );
      return Object.fromEntries(entries);
    },
    getByRecordTypeAsync: async (recordType) => {
      // If recordType is SubscriptionReal, rename to Subscription
      if (recordType === "SubscriptionReal") {
        recordType = "Subscription";
      }
      const entries = await Promise.all(
        Object.keys(items)
          .filter((key) => key.startsWith(`${recordType}.`))
          .map(async (key) => [key, await getAsync(key)] as const)
      );
      return Object.fromEntries(entries);
    },
    has: (id) => id in items,
  };
}

const registry = {
  renderers: buildRegistry(modules.renderers),
  actions: buildRegistry(modules.actions),
  records: buildRegistry(modules.records),
  queries: buildRegistry(modules.queries),
};

export const { renderers, actions, records, queries } = registry;
